import React, { useState, useEffect } from 'react';
import {
  Activity,
  Clock,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  XCircle,
  Zap,
  Calendar
} from 'lucide-react';
import { useSiteContext } from '../contexts/SiteContext';

const UptimeMonitoring = ({ user }) => {
  const { selectedSite } = useSiteContext();
  const [monitorData, setMonitorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (selectedSite) {
      fetchMonitorData();
    } else {
      setLoading(false);
      setError('Please select a site to view uptime monitoring');
    }
  }, [user, selectedSite]);

  const fetchMonitorData = async () => {
    if (!selectedSite) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/app/api/uptime/monitor.php?siteId=${selectedSite.id}`, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMonitorData(data.monitor);
      } else {
        setError(data.message || 'Failed to fetch monitor data');
      }
    } catch (error) {
      console.error('Error fetching monitor data:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatUptime = (uptime) => {
    return uptime ? uptime.toFixed(2) + '%' : 'N/A';
  };

  const formatResponseTime = (time) => {
    return time ? time.toFixed(2) + 'ms' : 'N/A';
  };

  // Format datetime to show "X minutes/hours/days ago" with 2-level precision
  const formatTimeAgo = (utcDatetime) => {
    if (!utcDatetime) return 'Never';

    // Parse UTC datetime and convert to local time
    const utcDate = new Date(utcDatetime + ' UTC');
    const now = new Date();
    const diffMs = now - utcDate;

    const totalSeconds = Math.floor(diffMs / 1000);
    const totalMinutes = Math.floor(totalSeconds / 60);
    const totalHours = Math.floor(totalMinutes / 60);
    const totalDays = Math.floor(totalHours / 24);

    // Calculate remainders for 2-level display
    const days = totalDays;
    const hours = totalHours % 24;
    const minutes = totalMinutes % 60;
    const seconds = totalSeconds % 60;

    // Build 2-level time string
    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} ${hours} hour${hours !== 1 ? 's' : ''} ago`;
    }
    if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    }
    if (minutes > 0) {
      return `${minutes} minute${minutes > 1 ? 's' : ''} ${seconds} second${seconds !== 1 ? 's' : ''} ago`;
    }
    return `${seconds} second${seconds !== 1 ? 's' : ''} ago`;
  };

  const calculateCurrentlyUpFor = (lastCheckDatetime, isOk) => {
    if (!lastCheckDatetime || !isOk) return 'Currently Down';

    // Parse UTC datetime and convert to local time
    const lastCheck = new Date(lastCheckDatetime + ' UTC');
    const now = new Date();
    const diffMs = now - lastCheck;

    const totalSeconds = Math.floor(diffMs / 1000);
    const totalMinutes = Math.floor(totalSeconds / 60);
    const totalHours = Math.floor(totalMinutes / 60);
    const totalDays = Math.floor(totalHours / 24);

    // Calculate remainders for 2-level display
    const days = totalDays;
    const hours = totalHours % 24;
    const minutes = totalMinutes % 60;

    // Build 2-level time string
    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} ${hours} hour${hours !== 1 ? 's' : ''}`;
    }
    if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  };

  // Format UTC datetime to local datetime string
  const formatLocalDateTime = (utcDatetime) => {
    if (!utcDatetime) return 'Never';
    const utcDate = new Date(utcDatetime + ' UTC');
    return utcDate.toLocaleString();
  };

  const calculateIncidents = (totalChecks, totalOkChecks) => {
    return totalChecks - totalOkChecks;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Uptime Monitoring</h1>
            <p className="mt-1 text-sm text-gray-500">
              Monitor your site's uptime and performance
            </p>
          </div>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 flex items-center">
          <AlertCircle className="h-6 w-6 text-yellow-600 mr-3" />
          <div>
            <h3 className="text-sm font-medium text-yellow-800">{error}</h3>
            {!selectedSite && (
              <p className="mt-1 text-sm text-yellow-700">
                Select a site from the dropdown above to view its uptime monitoring data.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!monitorData) {
    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Uptime Monitoring</h1>
            <p className="mt-1 text-sm text-gray-500">
              {selectedSite ? `Monitoring ${selectedSite.name}` : 'Monitor your site\'s uptime and performance'}
            </p>
          </div>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 flex items-center">
          <AlertCircle className="h-6 w-6 text-gray-400 mr-3" />
          <p className="text-sm text-gray-600">No monitoring data available</p>
        </div>
      </div>
    );
  }

  const currentlyUpFor = calculateCurrentlyUpFor(
    monitorData.last_not_ok_datetime||
    monitorData.main_ok_datetime||monitorData.last_check_datetime
    , monitorData.is_ok);
  const incidents = calculateIncidents(monitorData.total_checks, monitorData.total_ok_checks);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Uptime Monitoring</h1>
          <p className="mt-1 text-sm text-gray-500">
            {selectedSite ? `Monitoring ${selectedSite.name}` : 'Monitor your site\'s uptime and performance'}
          </p>
        </div>
        <button
          onClick={fetchMonitorData}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <Activity className="h-4 w-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Status Banner */}
      <div className={`${
        monitorData.is_ok
          ? 'bg-gradient-to-r from-green-50 to-green-100 border-green-200'
          : 'bg-gradient-to-r from-red-50 to-red-100 border-red-200'
      } border rounded-lg p-6`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {monitorData.is_ok ? (
              <CheckCircle className="h-8 w-8 text-green-600 mr-3" />
            ) : (
              <XCircle className="h-8 w-8 text-red-600 mr-3" />
            )}
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {monitorData.is_ok ? 'All Systems Operational' : 'System Down'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {monitorData.name} - {monitorData.target}
              </p>
            </div>
          </div>
          <div className={`px-4 py-2 rounded-full ${
            monitorData.is_ok
              ? 'bg-green-600 text-white'
              : 'bg-red-600 text-white'
          }`}>
            <span className="text-lg font-semibold">
              {monitorData.is_ok ? 'ONLINE' : 'OFFLINE'}
            </span>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Uptime */}
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6 border-2 border-green-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Uptime</span>
            <TrendingUp className="h-5 w-5 text-green-600" />
          </div>
          <div className="text-3xl font-bold text-gray-900">
            {formatUptime(monitorData.uptime)}
          </div>
          <div className="mt-2 text-xs text-gray-600">
            Based on {monitorData.total_checks} checks
          </div>
        </div>

        {/* Average Response Time */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 border-2 border-blue-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Avg Response Time</span>
            <Zap className="h-5 w-5 text-blue-600" />
          </div>
          <div className="text-3xl font-bold text-gray-900">
            {formatResponseTime(monitorData.average_response_time)}
          </div>
          <div className="mt-2 text-xs text-gray-600">
            Lower is better
          </div>
        </div>

        {/* Incidents */}
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-6 border-2 border-orange-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Incidents</span>
            <AlertCircle className="h-5 w-5 text-orange-600" />
          </div>
          <div className="text-3xl font-bold text-gray-900">
            {incidents}
          </div>
          <div className="mt-2 text-xs text-gray-600">
            Failed checks detected
          </div>
        </div>

        {/* Currently Up For */}
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-6 border-2 border-purple-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Currently Up For</span>
            <Clock className="h-5 w-5 text-purple-600" />
          </div>
          <div className="text-3xl font-bold text-gray-900">
            {currentlyUpFor}
          </div>
          <div className="mt-2 text-xs text-gray-600">
            Since last check
          </div>
        </div>
      </div>

      {/* Additional Details */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Monitor Details
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <Calendar className="h-5 w-5 text-gray-600 mr-2" />
                <span className="text-sm font-medium text-gray-700">Last Checked</span>
              </div>
              <div className="text-base text-gray-900">
                {formatTimeAgo(monitorData.last_check_datetime)}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {formatLocalDateTime(monitorData.last_check_datetime)}
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <Clock className="h-5 w-5 text-gray-600 mr-2" />
                <span className="text-sm font-medium text-gray-700">Check Interval</span>
              </div>
              <div className="text-base text-gray-900">
                {monitorData.settings?.check_interval_seconds
                  ? `${monitorData.settings.check_interval_seconds / 60} minutes`
                  : 'N/A'}
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <CheckCircle className="h-5 w-5 text-gray-600 mr-2" />
                <span className="text-sm font-medium text-gray-700">Successful Checks</span>
              </div>
              <div className="text-base text-gray-900">
                {monitorData.total_ok_checks} / {monitorData.total_checks}
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <XCircle className="h-5 w-5 text-gray-600 mr-2" />
                <span className="text-sm font-medium text-gray-700">Failed Checks</span>
              </div>
              <div className="text-base text-gray-900">
                {monitorData.total_not_ok_checks}
              </div>
            </div>
{/*
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <Activity className="h-5 w-5 text-gray-600 mr-2" />
                <span className="text-sm font-medium text-gray-700">Monitor Type</span>
              </div>
              <div className="text-base text-gray-900 capitalize">
                {monitorData.type}
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <Calendar className="h-5 w-5 text-gray-600 mr-2" />
                <span className="text-sm font-medium text-gray-700">Created</span>
              </div>
              <div className="text-base text-gray-900">
                {new Date(monitorData.datetime).toLocaleDateString()}
              </div>
            </div>
            */}
          </div>
        </div>
      </div>

      {/* Downtime Information */}
      {monitorData.downtime > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start">
            <AlertCircle className="h-6 w-6 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800">Downtime Detected</h3>
              <p className="mt-1 text-sm text-red-700">
                This site has experienced {formatUptime(monitorData.downtime)} downtime.
                Total incidents: {incidents}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UptimeMonitoring;
